import { cn } from '@/lib/utils';
import { STATUS_TONE_VARIANT, STORE_STATUS } from '@/lib/store-status';

export interface StatusBadgeProps {
    /** Used as both the style key AND the label if no label/variant provided */
    status: string;
    /** Optional override for display text */
    label?: string;
    /** Optional explicit variant to control the visual style independently of the status text */
    variant?: 'success' | 'warning' | 'error' | 'info' | 'neutral';
    className?: string;
    size?: 'sm' | 'md';
}

const BADGE_STYLES: Record<string, string> = {
    active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    inactive: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20',
    neutral: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20',
    pending: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    warning: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    error: 'bg-red-500/15 text-red-400 border-red-500/20',
    info: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
};

const DOT_STYLES: Record<string, string> = {
    active: 'bg-emerald-400 animate-pulse-subtle',
    success: 'bg-emerald-400 animate-pulse-subtle',
    pending: 'bg-amber-400',
    warning: 'bg-amber-400',
    error: 'bg-red-400',
    info: 'bg-blue-400',
    inactive: 'bg-zinc-400',
    neutral: 'bg-zinc-400',
};

const BADGE_LABELS: Record<string, string> = {
    active: 'Activo',
    inactive: 'Inactivo',
    pending: 'Pendiente',
    error: 'Error',
    warning: 'Advertencia',
    success: 'Éxito',
};

/**
 * StatusBadge — displays a styled badge with colored dot.
 *
 * Usage patterns:
 *   <StatusBadge status="active" />                          // green badge, label "Activo"
 *   <StatusBadge status="Pendiente" variant="warning" />     // amber badge, label "Pendiente"
 *   <StatusBadge status="active" label="En línea" />         // green badge, label "En línea"
 */
export function StatusBadge({ status, label, variant, className, size = 'sm' }: StatusBadgeProps) {
    const known = STORE_STATUS[status];
    const styleKey = variant || (known ? STATUS_TONE_VARIANT[known.tone] : status);
    const style = BADGE_STYLES[styleKey] || BADGE_STYLES.neutral;
    const dot = DOT_STYLES[styleKey] || DOT_STYLES.neutral;
    const displayLabel = label || known?.label || BADGE_LABELS[status] || status;

    return (
        <span
            className={cn(
                'inline-flex items-center rounded-full border font-medium',
                size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
                style,
                className
            )}
        >
            <span className={cn(
                'rounded-full mr-1.5 shrink-0',
                size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2',
                dot
            )} />
            {displayLabel}
        </span>
    );
}
